import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import { EMPTY_STRING } from "common/constants";
import routes from "common/routes";
import AppPage from "components/AppPage";
import AppPageContent from "components/AppPageContent";
import AppPageHeader from "components/AppPageHeader";
import { useFacilityBarns } from "components/BarnNumberPicker/useFacilityBarns";
import {
  withClickBlocker,
  WithClickBlockerProps
} from "components/BlockableClickContext";
import { getDateRangeQuery } from "components/DateRangeSelect/helpers";
import ErrorBoundary from "components/ErrorBoundary";
import Loader from "components/Loader";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import ScrollableMobileTabBar from "components/ScrollableMobileTabBar";
import { useSnackbar } from "components/SnackbarContext/SnackbarContext";
import ConfirmAssignmentDialog from "components/VetExamsAssignment/ConfirmAssignmentDialog";
import {
  examsRowDataMapper,
  filterVisibleCheckedWorkoutIds
} from "components/VetExamsTable/helper";
import { useRacehorse360Api, useSetQueryData } from "hooks/api";
import { useDefaultTab } from "hooks/useDefaultTab";
import { useInfiniteScroll } from "hooks/useInfiniteScroll";
import { DateRangeISO, DateRangeType } from "interfaces/DateRange";
import { tabs } from "interfaces/VetWorkoutsTab";
import {
  resetCheckedIds,
  setAssignCheckedIds,
  setExamCheckedIds,
  setFacilityBarns,
  setOrderBy,
  setPassCheckedIds,
  setSavingCheckedIds,
  setSearchQuery,
  setSelectedDateRange as setStoreSelectedDateRange,
  setSelectedTab as setStoreSelectedTab,
  setWorkoutRequestListRequestKey
} from "store/actions/vetWorkoutsPage";
import ConfirmSelectionsDialog from "./ConfirmSelectionsDialog";
import Header from "./Header";
import WorkoutsTable from "./WorkoutsTable";
import {
  columnSettings,
  commonWorkoutRequestSelectedFields,
  EColumnName
} from "./WorkoutsTable/options";
import {
  checkCurrentWorkoutsTableTab,
  getOrderByOptions,
  getOrderRules,
  WORKOUT_REQUEST_TOTAL_RESULTS_QUERY_KEY
} from "./helper";
import { useVetWorkoutsStore } from "./useVetWorkoutsStore";

import useStyles from "./styles";
import { useAppSelector } from "hooks/redux/useAppSelector";

const possibleVetRoutes = [
  routes.workoutRequests,
  routes.workoutExams,
  routes.workoutPassed,
  routes.workoutFailed,
  routes.workoutRejected
];

const VetWorkoutsPage = (props: WithClickBlockerProps) => {
  const { handleBlockableClick } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const { currentUser } = useLoggedInUser();
  const { showSuccessSnack, showErrorSnack } = useSnackbar();
  const { updateWorkoutRequests } = useSetQueryData();

  const tableBodyRef = useRef<HTMLDivElement>(null);

  const {
    assignedOnly,
    selectedFacility,
    selectedTab,
    order,
    orderBy,
    searchValue,
    assignCheckedIds,
    savingCheckedIds,
    examCheckedIds,
    passCheckedIds,
    convertedToDateSelectedDateRange
  } = useVetWorkoutsStore();

  useDefaultTab(possibleVetRoutes, null, selectedTab.path);
  const { facilityBarns, isLoading: isListBarnsLoading } = useFacilityBarns(
    selectedFacility?.id
  );
  const { filterWorkoutRequests } = useSetQueryData();
  const isInfiniteScrollOn = useAppSelector(
    state => state.queriesConfig.isInfiniteScrollOn
  );

  const [clearSearch, setClearSearch] = useState<boolean>(false);

  const { isRequestsTab, isRejectedTab, isExamsTab, isPassedTab } =
    checkCurrentWorkoutsTableTab(selectedTab.value);
  const isTabWithRequests = isRequestsTab || isRejectedTab;
  const isAssignAvailable = assignedOnly && !isTabWithRequests;

  const [isAssignToVetDialogOpen, setIsAssignToVetDialogOpen] =
    useState<boolean>(false);
  const [isOpenConfirmSelectionsPopup, setIsOpenConfirmSelectionsPopup] =
    useState<boolean>(false);

  const {
    useBulkQuickPassWorkoutRequest,
    useBulkRequireWorkoutRequestExam,
    useBulkAssignWorkoutRequestExam,
    useInfiniteListWorkoutRequests,
    useListWorkoutRequests
  } = useRacehorse360Api();

  const { mutateAsync: assignExam } = useBulkAssignWorkoutRequestExam();
  const { mutateAsync: quickPassRequests } = useBulkQuickPassWorkoutRequest();
  const { mutateAsync: requireExams } = useBulkRequireWorkoutRequestExam();

  useEffect(() => {
    return () => {
      dispatch(resetCheckedIds());
    };
  }, [dispatch, selectedFacility?.id]);

  const activeColumn = columnSettings.find(item =>
    item.columnNames.includes(orderBy)
  );

  const workoutRequestsQuery: racehorse360.IWorkoutRequestFilter = {
    facilityIds: [selectedFacility?.id],
    horseOrTrainerName: searchValue?.length
      ? { contains: searchValue }
      : undefined,
    statuses: selectedTab.requestStatuses,
    date: getDateRangeQuery(convertedToDateSelectedDateRange),
    horseExamSubFilter: isTabWithRequests
      ? null
      : {
          statuses: selectedTab.examStatuses,
          assignedVetIds: isAssignAvailable ? [currentUser.rh360Id] : [],
          horseExamTypes: [
            racehorse360.HorseExamType.HORSE_EXAM_TYPE_QUICK_PASS,
            racehorse360.HorseExamType.HORSE_EXAM_TYPE_FULL_EXAM
          ],
          results: selectedTab?.results,
          withWorkout: { value: true }
        }
  };

  const requestsOrderBy = getOrderByOptions(activeColumn?.orderBy, orderBy);

  const { data: workoutRequestsTotalResultsData } = useListWorkoutRequests(
    WORKOUT_REQUEST_TOTAL_RESULTS_QUERY_KEY,
    {
      query: workoutRequestsQuery,
      pagingOptions: {
        maxResults: 1,
        includeSummary: true
      }
    },
    {
      enabled: Boolean(selectedFacility?.id)
    }
  );

  const {
    isLoading: isListWorkoutRequestsLoading,
    isFetching,
    isRefetching,
    data: workoutRequestsData,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    compoundRequestKey
  } = useInfiniteListWorkoutRequests(
    `${currentUser.rh360Id}-${searchValue}-infinite-workout-requests`,
    {
      query: workoutRequestsQuery,
      pagingOptions: {
        maxResults: selectedTab.itemsPerPage,
        includeSummary: true
      },
      getOptions: {
        select: [
          ...commonWorkoutRequestSelectedFields,
          ...selectedTab.additionalSelectedFields
        ],
        orderBy: getOrderRules(requestsOrderBy, order)
      }
    },
    {
      enabled: Boolean(selectedFacility?.id)
    }
  );

  const preparedTableRows = workoutRequestsData?.pages
    ?.flat()
    .map(examsRowDataMapper);

  const visibleAssignCheckedWorkoutIds = filterVisibleCheckedWorkoutIds(
    preparedTableRows,
    assignCheckedIds
  );
  const visiblePassCheckedWorkoutIds = filterVisibleCheckedWorkoutIds(
    preparedTableRows,
    passCheckedIds
  );
  const visibleExamCheckedWorkoutIds = filterVisibleCheckedWorkoutIds(
    preparedTableRows,
    examCheckedIds
  );

  const getTabsWithTotalResults = () => {
    const totalNumber =
      workoutRequestsTotalResultsData?.pagingInfo?.totalResults;

    return tabs.map(tab => {
      const shouldShowTotalResultsForTab = [
        isRequestsTab,
        isExamsTab,
        isPassedTab
      ].some(Boolean);
      const isTabActive = tab.value === selectedTab.value;
      const shouldShowTotalResults = [
        shouldShowTotalResultsForTab,
        isTabActive,
        typeof totalNumber === "number"
      ].every(Boolean);

      if (shouldShowTotalResults) {
        return `${tab.name} (${totalNumber})`;
      }

      return tab.name;
    });
  };

  useInfiniteScroll(
    tableBodyRef,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isInfiniteScrollOn,
    true
  );

  const saveRequestsSelection = () => {
    const promises = [];

    if (visiblePassCheckedWorkoutIds.length) {
      promises.push(quickPassRequests({ ids: visiblePassCheckedWorkoutIds }));
    }

    if (visibleExamCheckedWorkoutIds.length) {
      promises.push(requireExams({ ids: visibleExamCheckedWorkoutIds }));
    }

    const checkedIds = [
      ...visiblePassCheckedWorkoutIds,
      ...visibleExamCheckedWorkoutIds
    ];

    dispatch(setSavingCheckedIds([...savingCheckedIds, ...checkedIds]));

    const clearSavingCheckedIds = () => {
      dispatch(
        setSavingCheckedIds(
          savingCheckedIds.filter(savingCheckedId => {
            return checkedIds.includes(savingCheckedId);
          })
        )
      );
    };

    Promise.all(promises)
      .then(data => {
        const updatedWorkoutRequestIds = data.flatMap(
          (res: racehorse360.IListWorkoutRequestsResponse) => {
            return res.workoutRequests.map(workoutRequest => workoutRequest.id);
          }
        );

        dispatch(setExamCheckedIds([]));
        dispatch(setPassCheckedIds([]));
        filterWorkoutRequests(
          updatedWorkoutRequestIds,
          WORKOUT_REQUEST_TOTAL_RESULTS_QUERY_KEY
        );
      })
      .catch(error => {
        console.error("ERROR", error);
      })
      .finally(() => {
        clearSavingCheckedIds();
      });
  };

  const saveExamsSelection = (vetId: string, assignCheckedIds: string[]) => {
    dispatch(setSavingCheckedIds([...savingCheckedIds, ...assignCheckedIds]));

    const clearSavingCheckedIds = () => {
      dispatch(
        setSavingCheckedIds(
          savingCheckedIds.filter(savingCheckedId => {
            return assignCheckedIds.includes(savingCheckedId);
          })
        )
      );
    };

    assignExam({
      ids: assignCheckedIds,
      assignedVetId: vetId,
      getOptions: {
        select: [
          ...commonWorkoutRequestSelectedFields,
          ...selectedTab.additionalSelectedFields
        ]
      }
    })
      .then(data => {
        const updatedWorkoutRequests = data.workoutRequests;

        if (updatedWorkoutRequests.length) {
          const assignedUser =
            updatedWorkoutRequests[0].workoutExam.assignedUser;
          const updatedWorkoutRequestIds =
            updatedWorkoutRequests.map(item => item.id) || [];
          const isAssignedToAnotherVet = vetId !== currentUser.rh360Id;

          if (assignedOnly && isAssignedToAnotherVet) {
            filterWorkoutRequests(
              updatedWorkoutRequestIds,
              WORKOUT_REQUEST_TOTAL_RESULTS_QUERY_KEY
            );
          } else {
            updateWorkoutRequests(updatedWorkoutRequestIds, {
              workoutExam: {
                assignedUser: assignedUser,
                assignedUserId: assignedUser.id
              }
            });
          }
        }

        showSuccessSnack("Vet Assigned");
        dispatch(setAssignCheckedIds([]));
      })
      .catch(error => {
        showErrorSnack(error.message);
        console.error(error);
      })
      .finally(() => {
        clearSavingCheckedIds();
      });
  };

  const openAssignToVetDialog = () => {
    setIsAssignToVetDialogOpen(true);
  };

  const handleTabChange = useCallback(
    (tabIndex: number) => {
      if (tabs[tabIndex].value === selectedTab.value) return;

      handleBlockableClick(() => {
        const currentActiveTab = tabs[tabIndex];
        const { isExamsTab } = checkCurrentWorkoutsTableTab(
          currentActiveTab.value
        );

        if (!convertedToDateSelectedDateRange) {
          const date: DateRangeISO = {
            Type: DateRangeType.Next3Days
          };

          dispatch(setStoreSelectedDateRange(date));
        }

        isExamsTab && dispatch(setOrderBy(EColumnName.TRAINER_LASTNAME));
        dispatch(setStoreSelectedTab(currentActiveTab));
        window.history.pushState(null, EMPTY_STRING, currentActiveTab.path);
        window.scrollTo(0, 0);
      });
    },
    [
      dispatch,
      convertedToDateSelectedDateRange,
      tabs,
      selectedTab.value,
      handleBlockableClick
    ]
  );

  const handleAssignmentDialogConfirm = (vetId: string) => {
    setIsAssignToVetDialogOpen(false);
    saveExamsSelection(vetId, visibleAssignCheckedWorkoutIds);
  };

  const handleAssignmentDialogClose = () => {
    setIsAssignToVetDialogOpen(false);
  };

  const handleClearSearch = useCallback(
    (value: boolean) => {
      dispatch(setSearchQuery(""));
      setClearSearch(value);
    },
    [dispatch]
  );

  const handleOpenConfirmSelectionsDialog = () => {
    setIsOpenConfirmSelectionsPopup(true);
  };

  const handleCloseConfirmSelectionsDialog = () => {
    setIsOpenConfirmSelectionsPopup(false);
  };

  const shouldShowMainLoader = [
    isListBarnsLoading,
    isListWorkoutRequestsLoading,
    !preparedTableRows,
    isFetching && !isFetchingNextPage && !isRefetching
  ].some(Boolean);

  const activeTabIndex = Math.max(
    0,
    tabs.findIndex(tab => tab.value === selectedTab.value)
  );

  useEffect(() => {
    dispatch(setFacilityBarns(facilityBarns));
  }, [facilityBarns]);

  useEffect(() => {
    dispatch(setWorkoutRequestListRequestKey(compoundRequestKey));
  }, [JSON.stringify(compoundRequestKey)]);

  const pageContent = shouldShowMainLoader ? (
    <Loader />
  ) : (
    <WorkoutsTable
      rows={preparedTableRows}
      isFetchingNextPage={isFetchingNextPage}
      isRefetching={isRefetching}
      tableBodyRef={tableBodyRef}
      searchValue={searchValue}
      onClearSearch={handleClearSearch}
    />
  );

  return (
    <AppPage className={classes.appPage}>
      <AppPageHeader className={classes.appPageHeader}>
        <Header
          workoutRequestsQuery={workoutRequestsQuery}
          openAssignToVetDialog={openAssignToVetDialog}
          openConfirmSelectionsDialog={handleOpenConfirmSelectionsDialog}
          clearSearch={clearSearch}
          onClearSearch={handleClearSearch}
          tableRows={preparedTableRows}
        />
        <ScrollableMobileTabBar
          className={classes.tabBar}
          tabs={getTabsWithTotalResults()}
          activeIndex={activeTabIndex}
          onActiveTabChange={handleTabChange}
        />
      </AppPageHeader>

      <AppPageContent>{pageContent}</AppPageContent>

      {isAssignToVetDialogOpen && (
        <ConfirmAssignmentDialog
          open={isAssignToVetDialogOpen}
          onClose={handleAssignmentDialogClose}
          onConfirm={handleAssignmentDialogConfirm}
          selectedItemsAmount={visibleAssignCheckedWorkoutIds.length}
          selectedFacilityId={selectedFacility?.id}
        />
      )}

      {isOpenConfirmSelectionsPopup && (
        <ErrorBoundary>
          <ConfirmSelectionsDialog
            open={true}
            onClose={handleCloseConfirmSelectionsDialog}
            onSaveRequestsSelection={saveRequestsSelection}
            workoutRequestQuery={workoutRequestsQuery}
            passCheckedIds={visiblePassCheckedWorkoutIds}
            examCheckedIds={visibleExamCheckedWorkoutIds}
          />
        </ErrorBoundary>
      )}
    </AppPage>
  );
};

export default React.memo(withClickBlocker(VetWorkoutsPage));
